(() => {
  if (customElements.get('cap-search-placeholder')) return;

  customElements.define('cap-search-placeholder', class extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        if (!this.readyListener) {
          this.readyListener = () => { this.readyListener = null; this.initialize(); };
          document.addEventListener('DOMContentLoaded', this.readyListener, { once: true });
        }
        return;
      }
      this.initialize();
    }

    initialize() {
      if (!this.isConnected || this.events) return;
      this.input = this.parentElement.querySelector('[data-cap-search-placeholder-input]');
      this.prefixElement = this.querySelector('[data-placeholder-prefix]');
      this.word = this.querySelector('[data-placeholder-word]');
      this.dots = this.querySelector('.cap-search-dots');
      this.measures = [...this.querySelectorAll('[data-placeholder-measure-word]')];
      if (!this.input || !this.prefixElement || !this.word || !this.dots || !this.measures.length) return;
      this.words = this.measures.map(element => element.textContent.trim().replace(/(?:\.{3}|…)$/, ''));
      this.measures.forEach((element, index) => { element.textContent = this.words[index]; });
      this.index = 0;
      this.word.textContent = this.words[0];
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.drawer = this.closest('drawer-element');
      this.details = this.closest('details');
      this.inView = true;
      this.pageHidden = false;
      const refresh = () => { this.measure(); this.sync(); };
      const sync = () => this.sync();

      ['focus', 'blur', 'input', 'change', 'search'].forEach(name => this.input.addEventListener(name, sync, options));
      // Native clear can keep focus and change the value without an input event.
      this.parentElement.addEventListener('click', event => {
        if (event.target.closest?.('[data-popdown-close]')) this.sync();
      }, options);
      document.addEventListener('visibilitychange', sync, options);
      this.motion.addEventListener('change', sync, options);
      window.addEventListener('resize', refresh, options);
      window.addEventListener('load', refresh, options);
      window.addEventListener('pagehide', () => { this.pageHidden = true; this.stop(); }, options);
      window.addEventListener('pageshow', () => { this.pageHidden = false; refresh(); }, options);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(refresh);
        this.resizeObserver.observe(this.input);
      }
      if (window.IntersectionObserver) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.inView = entries.some(entry => entry.isIntersecting);
          refresh();
        });
        this.intersectionObserver.observe(this.input);
      } else {
        window.addEventListener('scroll', sync, { ...options, passive: true });
      }
      if (this.drawer || this.details) {
        this.containerObserver = new MutationObserver(refresh);
        const container = this.drawer || this.details;
        this.containerObserver.observe(container, { attributes: true, attributeFilter: ['class', 'open'] });
        container.addEventListener('transitionend', refresh, options);
      }
      document.fonts?.addEventListener('loadingdone', refresh, options);
      document.fonts?.ready.then(() => { if (this.events && this.isConnected) refresh(); });
      refresh();
    }

    measure() {
      const input = this.input.getBoundingClientRect();
      if (!input.width || !input.height) return;
      const parent = this.parentElement.getBoundingClientRect();
      const style = getComputedStyle(this.input);
      ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontStretch', 'letterSpacing', 'textTransform', 'lineHeight'].forEach(property => {
        this.style[property] = style[property];
      });
      const left = (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.paddingLeft) || 0);
      let right = (parseFloat(style.borderRightWidth) || 0) + (parseFloat(style.paddingRight) || 0);
      // Keep the decorative text out of the existing clear/submit button area.
      [...this.parentElement.children].filter(element => element.tagName === 'BUTTON' && !element.classList.contains('visually-hidden')).forEach(button => {
        const box = button.getBoundingClientRect();
        if (box.width && box.left >= input.left + input.width / 2) right = Math.max(right, input.right - box.left);
      });
      const available = Math.max(0, input.width - left - right);
      this.style.left = `${input.left - parent.left + left}px`;
      this.style.top = `${input.top - parent.top + input.height / 2}px`;
      this.style.width = `${available}px`;
      const longest = Math.ceil(Math.max(...this.measures.map(element => element.getBoundingClientRect().width)) + this.dots.getBoundingClientRect().width);
      this.style.setProperty('--cap-placeholder-word-width', `${longest}px`);
      const gap = parseFloat(getComputedStyle(this).columnGap) || parseFloat(style.fontSize) * 0.28;
      const full = this.querySelector('[data-placeholder-full]');
      this.prefixElement.textContent = full.getBoundingClientRect().width + longest + gap <= available ? full.textContent : 'Search';
      this.setAttribute('data-ready', '');
    }

    visible() {
      if (document.hidden || this.pageHidden || !this.inView || !this.isConnected) return false;
      if (this.drawer && !this.drawer.classList.contains('is-open')) return false;
      if (this.details && !this.details.open) return false;
      const box = this.input.getBoundingClientRect();
      const style = getComputedStyle(this.input);
      return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < window.innerHeight &&
        box.right > 0 && box.left < window.innerWidth && style.visibility !== 'hidden' && style.display !== 'none';
    }

    sync() {
      if (this.motion.matches || !this.visible()) {
        this.stop();
        if (this.motion.matches) { this.index = 0; this.word.textContent = this.words[0]; }
        return;
      }
      this.toggleAttribute('data-placeholder-animate', this.canAnimate());
      if (!this.canAnimate()) this.cancelFade();
      if (!this.interval) this.interval = setInterval(() => this.advance(), 3000);
    }

    canAnimate() {
      return !this.input.value && (document.activeElement !== this.input || this.hasAttribute('data-show-when-focused'));
    }

    advance() {
      if (!this.visible() || this.motion.matches) { this.sync(); return; }
      this.toggleAttribute('data-placeholder-animate', this.canAnimate());
      if (!this.canAnimate()) return;
      this.word.classList.add('is-fading');
      this.fadeTimer = setTimeout(() => {
        this.fadeTimer = null;
        if (this.visible() && !this.motion.matches && this.canAnimate()) {
          this.index = (this.index + 1) % this.words.length;
          this.word.textContent = this.words[this.index];
        }
        this.word.classList.remove('is-fading');
      }, 300);
    }

    cancelFade() {
      clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
      this.word?.classList.remove('is-fading');
    }

    stop() {
      this.removeAttribute('data-placeholder-animate');
      clearInterval(this.interval);
      this.interval = null;
      this.cancelFade();
    }

    disconnectedCallback() {
      if (this.readyListener) document.removeEventListener('DOMContentLoaded', this.readyListener);
      this.readyListener = null;
      this.stop();
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.containerObserver?.disconnect();
    }
  });
})();
